/**
 * CardPacksPage — BESC Draw
 *
 * Flow:
 *   select → opening (tx pending, pack shakes) →
 *   tearing (swipe-to-open gesture) →
 *   reveal  (face-down fan/grid, tap each card) →
 *   history | collection | cashout | referral
 */

import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useReadContract, useReadContracts, useWriteContract, useBalance, usePublicClient } from "wagmi";
import { motion, AnimatePresence } from "framer-motion";
import { keccak256, stringToHex, decodeEventLog, type Address } from "viem";
import confetti from "canvas-confetti";

import Footer from "components/Footer/Footer";
import useWallet from "lib/wallets/useWallet";
import { helperToast } from "lib/helperToast";

import { CARD_PACK_SHOP_ABI, ERC20_ABI } from "./CardPackShopABI";
import { usePokemonTCG, type LiveCard } from "./hooks/usePokemonTCG";
import { drawPackCards, shuffleForReveal, buildTierPool } from "./hooks/usePackDraw";
import { PackSelector, TIER_DATA, TOKEN_SYMBOLS, TOKENS } from "./components/PackSelector";
import { Pack3D } from "./components/PackSelector";
import { PokemonCard, RARITY_COLOR } from "./components/PokemonCard";
import { GrailScreen } from "./components/GrailScreen";
import { LiveFeed, LiveTicker, pushRealPull } from "./components/LiveFeed";
import { BattlesTab } from "./components/BattlesTab";
import { BattleArena, type BattleData } from "./components/BattleArena";
import { SlabView } from "./components/SlabView";
import { DEMO_MODE, DEMO_BLOCKED_MSG, randomSeed } from "./demoMode";

import "./CardPacksPage.scss";

// ─── Constants ───────────────────────────────────────────────────────────────
const SHOP_ADDRESS = (import.meta.env.VITE_CARD_PACK_SHOP_ADDRESS ??
  "0x0000000000000000000000000000000000000000") as Address;

const TOKEN_ADDRESSES: Record<number, Address> = {
  0: "0xB54aD626E127f0f228dBeab6F2A61e8e6e029A4B", // BUSDC
  1: "0xFA4D586E7bF3E89909DB7ACC769af33731aC6355", // WBNB
  2: "0x86fc715AF0231b9eeA8d6dfcFf5e91E776E270A4", // WETH
};

// Referral tier thresholds and commissions
const REF_TIERS = [
  { label: "Bronze",  min: 0,  t1: 1,   t2: 0,   color: "#cd7f32", bg: "rgba(205,127,50,0.12)"  },
  { label: "Silver",  min: 10, t1: 2,   t2: 0,   color: "#c0c0c0", bg: "rgba(192,192,192,0.12)" },
  { label: "Gold",    min: 25, t1: 3,   t2: 0.5, color: "#ffd700", bg: "rgba(255,215,0,0.12)"   },
  { label: "Diamond", min: 50, t1: 5,   t2: 1,   color: "#b9f2ff", bg: "rgba(185,242,255,0.12)" },
] as const;

function getRefTier(count: number) {
  for (let i = REF_TIERS.length - 1; i >= 0; i--) {
    if (count >= REF_TIERS[i].min) return REF_TIERS[i];
  }
  return REF_TIERS[0];
}

// ─── Types ───────────────────────────────────────────────────────────────────
type Phase = "select" | "opening" | "tearing" | "spin" | "reveal" | "batch" | "history" | "collection" | "cashout" | "referral" | "battles";

interface DrawnPack {
  packId:           bigint;
  tier:             number;
  payToken:         number;
  seed:             string;
  cards:            LiveCard[];
  visualCards:      LiveCard[];
  timestamp:        number;
  soldBack:         boolean;
  shipped:          boolean;
  shipStatus?:      "pending" | "processing" | "shipped" | "delivered";
  trackingRef?:     string;
  // Card value signature (cached from backend, used for on-chain sell-back)
  cardValueUsd18?:  string;   // BigInt as string, e.g. "750000000000000000" for $0.75
  cardValueSig?:    string;   // 65-byte ECDSA hex signature from owner/valueOracle
  demo?:            boolean;  // drawn off-chain in demo mode — never redeemable
}

type CashoutFlow = "sell" | "ship" | null;

// ─── Sell-back value breakdown ────────────────────────────────────────────────
// refundToken: 0=BUSDC 1=WBNB 2=WETH 3=BESC 4=GEM(75%)
// cardUsd: the market value of the specific card the user pulled (used for refund calc)
function getSellBreakdown(pack: DrawnPack, refundToken: number, cardUsd?: number) {
  const baseUsd    = cardUsd ?? (pack.cards[0]?.approxValueUsd ?? 0);
  const isGem      = refundToken === 4;
  // Cash: 100% of card FMV (contract sellbackBps = 10_000)
  // GEM:  75% of card FMV in GEM (contract sellbackGemBps = 7_500, 100 GEM = $1)
  const pct        = isGem ? 0.75 : 1.0;
  return {
    cardUsd:     baseUsd,
    tokenBack:   baseUsd * pct,
    gemsBack:    baseUsd * 0.75 * 100,
    pct,
    isGem,
    tokenSymbol: isGem ? "GEM" : (TOKEN_SYMBOLS[refundToken] ?? "BUSDC"),
  };
}

// ─── Component ───────────────────────────────────────────────────────────────
export function CardPacksPage() {
  const { account } = useWallet();
  const tcg = usePokemonTCG();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  // ── Phase + selection ─────────────────────────────────────────────────────
  const [phase,         setPhase]         = useState<Phase>("select");
  const [selectedTier,  setSelectedTier]  = useState(1);
  const [selectedToken, setSelectedToken] = useState(0);
  const [volatility,    setVolatility]    = useState<"normal" | "high">("normal");

  // ── v4 new state ──────────────────────────────────────────────────────────
  const [packCount,         setPackCount]         = useState(1);
  const [giftMode,          setGiftMode]          = useState(false);
  const [giftRecipient,     setGiftRecipient]     = useState("");
  const [promoCode,         setPromoCode]         = useState("");
  const [isClaimingBonus,   setIsClaimingBonus]   = useState(false);
  const [refVanityCode,     setRefVanityCode]     = useState("");
  const [refCodeInput,      setRefCodeInput]      = useState("");   // input for setting vanity code
  const [refCodeByInput,    setRefCodeByInput]    = useState("");   // input for registering by code
  const [isSettingCode,     setIsSettingCode]     = useState(false);
  const [isRegByCode,       setIsRegByCode]       = useState(false);
  const [batchPacks,        setBatchPacks]        = useState<DrawnPack[]>([]);

  // ── Battles state ─────────────────────────────────────────────────────────
  const [activeBattle, setActiveBattle] = useState<BattleData | null>(null);

  // ── Pack state ────────────────────────────────────────────────────────────
  const [currentPack,  setCurrentPack]  = useState<DrawnPack | null>(null);
  const [history,      setHistory]      = useState<DrawnPack[]>(() => {
    try {
      const raw = localStorage.getItem("besc_draw_history_v2");
      if (!raw) return [];
      return (JSON.parse(raw) as any[]).map((p) => ({ ...p, packId: BigInt(p.packId) }));
    } catch { return []; }
  });
  const [grailCard,    setGrailCard]    = useState<LiveCard | null>(null);
  const [detailCard,   setDetailCard]   = useState<LiveCard | null>(null);
  const [flashRarity,  setFlashRarity]  = useState<string | null>(null);

  // Persist history to localStorage (bigint → string for JSON)
  useEffect(() => {
    try {
      const toSave = history.map((p) => ({ ...p, packId: p.packId.toString() }));
      localStorage.setItem("besc_draw_history_v2", JSON.stringify(toSave));
    } catch {}
  }, [history]);

  // ── Pick-one mechanic ─────────────────────────────────────────────────────
  const [pickedCard,   setPickedCard]   = useState<LiveCard | null>(null);
  const [cardFlipped,  setCardFlipped]  = useState(false);
  const spinViewportRef = useRef<HTMLDivElement>(null);

  // ── Tear gesture state ────────────────────────────────────────────────────
  const [tearProgress,  setTearProgress]  = useState(0);
  const [tearComplete,  setTearComplete]  = useState(false);
  const swipeRef = useRef({ startX: 0, startY: 0, active: false });

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [isOpening,   setIsOpening]   = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSelling,   setIsSelling]   = useState(false);
  const [isClaiming,  setIsClaiming]  = useState(false);

  // ── Cash-out modal ────────────────────────────────────────────────────────
  const [cashoutPack,      setCashoutPack]      = useState<DrawnPack | null>(null);
  const [cashoutFlow,      setCashoutFlow]      = useState<CashoutFlow>(null);
  const [sellRefundToken,  setSellRefundToken]  = useState(0); // 0=BUSDC..4=GEM
  const [sellConfirmStep,  setSellConfirmStep]  = useState<"review" | "done">("review");

  // ── Ship modal ────────────────────────────────────────────────────────────
  const [shipForm,    setShipForm]    = useState({ name:"", line1:"", city:"", country:"", zip:"" });
  const [shipStep,    setShipStep]    = useState<"form" | "review" | "done">("form");

  // ── Collection filters ────────────────────────────────────────────────────
  const [collSearch,  setCollSearch]  = useState("");
  const [collRarity,  setCollRarity]  = useState("all");
  const [collSort,    setCollSort]    = useState<"value-desc" | "value-asc" | "name" | "rarity">("value-desc");
  const [collView,    setCollView]    = useState<"grid" | "list">("grid");

  // ── Referral ──────────────────────────────────────────────────────────────
  const [refCopied,      setRefCopied]      = useState(false);
  const [refClaimToken,  setRefClaimToken]  = useState(0); // 0=BUSDC 1=WBNB 2=WETH 3=BESC

  // ── GEM cashout ───────────────────────────────────────────────────────────
  const [gemCashoutAmt,   setGemCashoutAmt]   = useState("");
  const [gemCashoutToken, setGemCashoutToken] = useState(0);
  const [isGemCashing,    setIsGemCashing]    = useState(false);

  const tierData  = TIER_DATA[selectedTier];
  const usdPrice  = parseFloat(tierData.prices[0].replace(/[$,]/g, ""));
  const tokenAddr = TOKEN_ADDRESSES[selectedToken];

  // ── Pack prices + oracle USD prices, sourced from the deployed contract ──────
  const promoHash = promoCode.trim()
    ? keccak256(stringToHex(promoCode.trim())) as `0x${string}`
    : undefined;

  const { data: contractReads } = useReadContracts({
    contracts: [
      // 0..4 packPrice per token for current tier
      ...([0, 1, 2, 3, 4].map((t) => ({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
        functionName: "packPrice", args: [selectedTier, t],
      })) as any),
      // 5..8 tokenUsdPrice for tokens 0..3
      ...([0, 1, 2, 3].map((t) => ({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
        functionName: "tokenUsdPrice", args: [t],
      })) as any),
      // 9 packCount (global)
      { address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "packCount", args: [] },
      // 10 jackpotInterval
      { address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "jackpotInterval", args: [] },
      // 11 maxBatchSize
      { address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "maxBatchSize", args: [] },
      // 12 paused
      { address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "paused", args: [] },
      // 13..19 tierMaxSupply[0..6]
      ...([0,1,2,3,4,5,6].map((t) => ({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
        functionName: "tierMaxSupply", args: [t],
      })) as any),
      // 20..26 tierSupplyUsed[0..6]
      ...([0,1,2,3,4,5,6].map((t) => ({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
        functionName: "tierSupplyUsed", args: [t],
      })) as any),
    ],
    query: { refetchInterval: 30_000 },
  });

  const packPrices: (bigint | undefined)[] = [0,1,2,3,4].map(
    (i) => contractReads?.[i]?.result as bigint | undefined
  );
  const globalPackCount = contractReads?.[9]?.result != null ? Number(contractReads[9].result as bigint) : undefined;
  const jackpotInterval = contractReads?.[10]?.result != null ? Number(contractReads[10].result as bigint) : undefined;
  const maxBatchSize    = contractReads?.[11]?.result != null ? Number(contractReads[11].result as bigint) : 20;
  // contractReads[12] = shopPaused (unused in UI; contract enforces it on-chain)
  const tierMaxSupplies = [0,1,2,3,4,5,6].map((i) => contractReads?.[13+i]?.result != null ? Number(contractReads[13+i]!.result as bigint) : 0);
  const tierSupplyUseds = [0,1,2,3,4,5,6].map((i) => contractReads?.[20+i]?.result != null ? Number(contractReads[20+i]!.result as bigint) : 0);

  const tierSoldOut = tierMaxSupplies[selectedTier] > 0 && tierSupplyUseds[selectedTier] >= tierMaxSupplies[selectedTier];

  // Promo code info
  const { data: promoData } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "getPromoInfo",
    args:         promoHash ? [promoHash] : undefined,
    query:        { enabled: !!promoHash, refetchInterval: 15_000 },
  }) as { data: [bigint, bigint, bigint, bigint, boolean] | undefined };

  const promoDiscount = promoData && promoData[4] ? Number(promoData[0]) : 0; // basis points

  // Pack price with promo applied
  const { data: promoPriceData } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "packPriceWithPromo",
    args:         account && promoHash ? [selectedTier, selectedToken, promoHash, account as Address] : undefined,
    query:        { enabled: !!account && !!promoHash && promoDiscount > 0, refetchInterval: 15_000 },
  }) as { data: bigint | undefined };

  // Effective prices: use promo price if available, else base price
  const effectivePackPrices: (bigint | undefined)[] = packPrices.map((p, i) =>
    i === selectedToken && promoPriceData != null ? promoPriceData : p
  );

  // Daily bonus reads
  const { data: dailyBonusData, refetch: refetchDailyBonus } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "canClaimDailyBonus",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account, refetchInterval: 30_000 },
  }) as { data: [boolean, bigint] | undefined; refetch: () => void };

  const canClaimBonus  = dailyBonusData?.[0] ?? false;
  const nextClaimAt    = dailyBonusData?.[1] != null ? Number(dailyBonusData[1]) * 1000 : null;

  // My vanity referral code
  const { data: myCodeData, refetch: refetchMyCode } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "myReferralCode",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account, refetchInterval: 60_000 },
  }) as { data: `0x${string}` | undefined; refetch: () => void };

  const myVanityCode = myCodeData && myCodeData !== "0x0000000000000000000000000000000000000000000000000000000000000000"
    ? Buffer.from(myCodeData.slice(2), "hex").toString("utf8").replace(/\0/g, "")
    : null;

  // My referrer (to know if already set)
  const { data: myReferrer } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "referrers",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account, refetchInterval: 60_000 },
  }) as { data: Address | undefined };

  const priceRaw: string = (effectivePackPrices[selectedToken] ?? packPrices[selectedToken] ?? 0n).toString();
  const totalPriceRaw: string = ((effectivePackPrices[selectedToken] ?? packPrices[selectedToken] ?? 0n) * BigInt(packCount)).toString();

  // ── Contract reads ────────────────────────────────────────────────────────
  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address:      tokenAddr,
    abi:          ERC20_ABI,
    functionName: "allowance",
    args:         account ? [account as Address, SHOP_ADDRESS] : undefined,
    query:        { enabled: !!account && selectedToken !== 3 && selectedToken !== 4 },
  });

  const { data: balance } = useReadContract({
    address:      tokenAddr,
    abi:          ERC20_ABI,
    functionName: "balanceOf",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account && selectedToken !== 3 && selectedToken !== 4, refetchInterval: 10_000 },
  });

  // Native BESC balance — fetched via wagmi's useBalance (msg.value path, payToken=3)
  const { data: nativeBalance } = useBalance({
    address: account as Address | undefined,
    query:   { enabled: !!account && selectedToken === 3, refetchInterval: 10_000 },
  });

  // GEM balance via the deployed GemToken ERC-20 (payToken=4 + dropdown display)
  const GEM_TOKEN_ADDRESS = (import.meta.env.VITE_GEM_TOKEN_ADDRESS || "") as `0x${string}` | "";
  const { data: gemBalance } = useReadContract({
    address:      GEM_TOKEN_ADDRESS || undefined,
    abi:          ERC20_ABI,
    functionName: "balanceOf",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account && !!GEM_TOKEN_ADDRESS, refetchInterval: 15_000 },
  });

  // Referral stats from contract
  const { data: refStats, refetch: refetchRefStats } = useReadContract({
    address:      SHOP_ADDRESS,
    abi:          CARD_PACK_SHOP_ABI,
    functionName: "getReferralStats",
    args:         account ? [account as Address] : undefined,
    query:        { enabled: !!account, refetchInterval: 30_000 },
  }) as { data: [bigint, bigint, bigint, bigint] | undefined; refetch: () => void };

  const needsApproval = !DEMO_MODE && selectedToken !== 3 && selectedToken !== 4 &&
    (!allowance || (allowance as bigint) < BigInt(totalPriceRaw));

  // Pick the right balance source for each pay-token:
  //   0–2 BUSDC/WBNB/WETH → ERC20 balanceOf
  //   3   BESC native     → wagmi useBalance
  //   4   GEM             → GemToken ERC20 balanceOf
  const activeBalance: bigint | null =
    selectedToken === 3 ? (nativeBalance ? nativeBalance.value : null)
    : selectedToken === 4 ? (gemBalance != null ? (gemBalance as bigint) : null)
    : (balance != null ? (balance as bigint) : null);
  const hasBalance = DEMO_MODE || (activeBalance != null && activeBalance >= BigInt(totalPriceRaw));

  // ── Derived referral values ───────────────────────────────────────────────
  const refCode  = account ? `BESC${account.slice(2,8).toUpperCase()}` : "BESC------";
  const refOrigin = typeof window !== "undefined"
    ? window.location.origin
    : "https://moneyxpro.com";
  const refLink  = `${refOrigin}/#/card-packs?ref=${refCode}`;
  const t1Count  = refStats ? Number(refStats[0]) : 0;
  const t2Count  = refStats ? Number(refStats[1]) : 0;
  const totalEarned  = refStats ? Number(refStats[2]) / 1e18 : 0;
  const claimable    = refStats ? Number(refStats[3]) / 1e18 : 0;
  const refTier      = getRefTier(t1Count);
  const nextTier     = REF_TIERS[REF_TIERS.indexOf(refTier as any) + 1];
  const tierProgress = nextTier
    ? Math.min(100, ((t1Count - refTier.min) / (nextTier.min - refTier.min)) * 100)
    : 100;

  // ── Collection derived ────────────────────────────────────────────────────
  const allCollCards = useMemo(() => {
    let cards = history.flatMap((p) => p.cards.map((c) => ({ ...c, _pack: p })));
    if (collSearch) {
      const q = collSearch.toLowerCase();
      cards = cards.filter((c) => c.name.toLowerCase().includes(q) || c.setName?.toLowerCase().includes(q));
    }
    if (collRarity !== "all") cards = cards.filter((c) => c.rarity === collRarity);
    switch (collSort) {
      case "value-asc":  return cards.sort((a, b) => a.approxValueUsd - b.approxValueUsd);
      case "name":       return cards.sort((a, b) => a.name.localeCompare(b.name));
      case "rarity": {
        const order = ["graded","ultra","holo","rare","uncommon","common"];
        return cards.sort((a, b) => order.indexOf(a.rarity) - order.indexOf(b.rarity));
      }
      default:           return cards.sort((a, b) => b.approxValueUsd - a.approxValueUsd);
    }
  }, [history, collSearch, collRarity, collSort]);

  const collTotalValue  = allCollCards.reduce((s, c) => s + c.approxValueUsd, 0);
  const collGraded      = allCollCards.filter((c) => c.rarity === "graded").length;
  const collUltra       = allCollCards.filter((c) => c.rarity === "ultra").length;
  const collAltArt      = allCollCards.filter((c) => c.isAltArt).length;

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = useCallback(async () => {
    if (!account) return;
    setIsApproving(true);
    try {
      // Approve 20x the per-pack price to avoid repeated approvals for batches
      await writeContractAsync({
        address: tokenAddr, abi: ERC20_ABI, functionName: "approve",
        args: [SHOP_ADDRESS, BigInt(priceRaw) * 20n],
      });
      helperToast.success("Approved! You can now open packs.");
      setTimeout(() => refetchAllowance(), 3000);
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Approval failed");
    } finally {
      setIsApproving(false);
    }
  }, [account, tokenAddr, priceRaw, writeContractAsync, refetchAllowance]);

  // ── Open pack (single or batch, normal or HV, gift or self, promo or plain) ──
  const handleOpenPack = useCallback(async () => {
    if ((!account && !DEMO_MODE) || !tcg.pool) return;
    setIsOpening(true);
    setPhase("opening");
    try {
      const nativeValue = selectedToken === 3 ? BigInt(totalPriceRaw) : undefined;
      const isBatch     = packCount > 1;
      const isGift      = giftMode && /^0x[0-9a-fA-F]{40}$/.test(giftRecipient);
      const isHV        = volatility === "high";
      const hasPromo    = !!promoHash && promoDiscount > 0;

      type PackOpenedArgs = { packId: bigint; tier: number; payToken: number; seed: bigint };
      const openedEvents: PackOpenedArgs[] = [];
      let txHash = "";

      if (DEMO_MODE) {
        // Off-chain draw: no wallet, no payment. Negative ids never collide with on-chain packs.
        await new Promise((r) => setTimeout(r, 1200));
        const base = BigInt(Date.now()) * 100n;
        for (let idx = 0; idx < packCount; idx++) {
          openedEvents.push({ packId: -(base + BigInt(idx)), tier: selectedTier, payToken: selectedToken, seed: randomSeed() });
        }
      } else {
        if (isGift && isBatch) {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "giftPackBatch",
            args: [giftRecipient as Address, selectedTier, selectedToken, packCount],
            value: nativeValue,
          });
        } else if (isGift) {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
            functionName: isHV ? "giftPackHV" : "giftPack",
            args: [giftRecipient as Address, selectedTier, selectedToken],
            value: nativeValue,
          });
        } else if (isBatch && hasPromo) {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "openPackBatchWithPromo",
            args: [selectedTier, selectedToken, packCount, promoHash!],
            value: nativeValue,
          });
        } else if (isBatch) {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
            functionName: isHV ? "openPackBatchHV" : "openPackBatch",
            args: [selectedTier, selectedToken, packCount],
            value: nativeValue,
          });
        } else if (hasPromo) {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "openPackWithPromo",
            args: [selectedTier, selectedToken, promoHash!],
            value: nativeValue,
          });
        } else {
          txHash = await writeContractAsync({
            address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI,
            functionName: isHV ? "openPackHV" : "openPack",
            args: [selectedTier, selectedToken],
            value: nativeValue,
          });
        }

        // Wait for receipt and parse real packId + seed from PackOpened event
        const receipt = await publicClient!.waitForTransactionReceipt({ hash: txHash as `0x${string}`, confirmations: 1 });

        // Parse PackOpened event(s) from receipt
        for (const log of receipt.logs) {
          try {
            const decoded = decodeEventLog({ abi: CARD_PACK_SHOP_ABI, eventName: "PackOpened", data: log.data, topics: log.topics as any });
            openedEvents.push(decoded.args as unknown as PackOpenedArgs);
          } catch { /* not a PackOpened log */ }
        }
      }

      const pool = buildTierPool(tcg.pool, selectedTier);

      if (isBatch) {
        const newPacks: DrawnPack[] = Array.from({ length: packCount }, (_, idx) => {
          const ev      = openedEvents[idx];
          const seed    = ev ? `0x${ev.seed.toString(16).padStart(64,"0")}` : keccak256(stringToHex(`${txHash}-${idx}`));
          const packId  = ev ? ev.packId : BigInt("0x" + txHash.slice(-16)) + BigInt(idx);
          const cards   = drawPackCards(seed, selectedTier, pool, volatility);
          const visual  = shuffleForReveal(cards, ev ? ev.seed : BigInt("0x" + txHash.slice(2, 18)));
          return {
            packId,
            tier:      selectedTier,
            payToken:  selectedToken,
            seed,
            cards,
            visualCards: visual,
            timestamp: Date.now(),
            soldBack:  false,
            shipped:   false,
            demo:      DEMO_MODE,
          };
        });

        setBatchPacks(newPacks);
        setHistory((h) => [...newPacks, ...h]);

        confetti({ particleCount: 80 * packCount, spread: 140, origin: { x:0.5, y:0.6 },
          colors: [TIER_DATA[selectedTier].accent, "#fff", "#818cf8"], ticks: 250 });

        const bestPack = newPacks.reduce((b, p) => {
          const v = p.cards.reduce((s,c) => s + c.approxValueUsd, 0);
          return v > b.cards.reduce((s,c) => s + c.approxValueUsd, 0) ? p : b;
        }, newPacks[0]);
        const bestCard = bestPack.cards.reduce((b,c) => c.approxValueUsd > b.approxValueUsd ? c : b, bestPack.cards[0]);
        if (bestCard && account) pushRealPull(account, bestCard, selectedTier);

        setPhase("batch");
      } else {
        const ev          = openedEvents[0];
        const seedBigInt  = ev ? ev.seed : BigInt("0x" + txHash.slice(2, 18));
        const seed        = ev ? `0x${ev.seed.toString(16).padStart(64,"0")}` : txHash.slice(0, 66);
        const realPackId  = ev ? ev.packId : BigInt("0x" + txHash.slice(-16));
        const cards       = drawPackCards(seed, selectedTier, pool, volatility);
        const visualCards = shuffleForReveal(cards, seedBigInt);

        const pack: DrawnPack = {
          packId:    realPackId,
          tier:      selectedTier,
          payToken:  selectedToken,
          seed,
          cards,
          visualCards,
          timestamp: Date.now(),
          soldBack:  false,
          shipped:   false,
          demo:      DEMO_MODE,
        };

        setCurrentPack(pack);
        setPickedCard(null);
        setCardFlipped(false);
        setHistory((h) => [pack, ...h]);

        const bestCard = cards.reduce((best, c) =>
          c.approxValueUsd > best.approxValueUsd ? c : best, cards[0]);
        if (bestCard && account) pushRealPull(account, bestCard, selectedTier);

        setPhase("tearing");
      }
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Failed to open pack");
      setPhase("select");
    } finally {
      setIsOpening(false);
    }
  }, [account, tcg.pool, selectedTier, selectedToken, totalPriceRaw, packCount,
      giftMode, giftRecipient, volatility, promoHash, promoDiscount, writeContractAsync]);

  // ── Daily GEM bonus ───────────────────────────────────────────────────────
  const handleClaimDailyBonus = useCallback(async () => {
    if (DEMO_MODE) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    if (!account) return;
    setIsClaimingBonus(true);
    try {
      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "claimDailyBonus", args: [],
      });
      helperToast.success("Daily GEM bonus claimed!");
      setTimeout(() => refetchDailyBonus(), 3000);
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Claim failed");
    } finally {
      setIsClaimingBonus(false);
    }
  }, [account, writeContractAsync, refetchDailyBonus]);

  // ── Set vanity referral code ───────────────────────────────────────────────
  const handleSetReferralCode = useCallback(async () => {
    if (!account || !refCodeInput.trim()) return;
    setIsSettingCode(true);
    try {
      const codeBytes32 = keccak256(stringToHex(refCodeInput.trim())) as `0x${string}`;
      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "setReferralCode",
        args: [codeBytes32],
      });
      helperToast.success(`Code "${refCodeInput.trim()}" claimed!`);
      setRefVanityCode(refCodeInput.trim());
      setTimeout(() => refetchMyCode(), 3000);
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Failed to claim code");
    } finally {
      setIsSettingCode(false);
    }
  }, [account, refCodeInput, writeContractAsync, refetchMyCode]);

  // ── Register referrer by vanity code ─────────────────────────────────────
  const handleRegisterByCode = useCallback(async () => {
    if (!account || !refCodeByInput.trim()) return;
    setIsRegByCode(true);
    try {
      const codeBytes32 = keccak256(stringToHex(refCodeByInput.trim())) as `0x${string}`;
      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "setReferrerByCode",
        args: [codeBytes32],
      });
      helperToast.success("Referrer registered by code!");
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Failed to register");
    } finally {
      setIsRegByCode(false);
    }
  }, [account, refCodeByInput, writeContractAsync]);

  // ── Swipe gesture ─────────────────────────────────────────────────────────
  const onSwipeDown = useCallback((e: React.PointerEvent) => {
    swipeRef.current = { startX: e.clientX, startY: e.clientY, active: true };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onSwipeMove = useCallback((e: React.PointerEvent) => {
    if (!swipeRef.current.active) return;
    const dx = Math.abs(e.clientX - swipeRef.current.startX);
    const dy = Math.abs(e.clientY - swipeRef.current.startY);
    if (dy > dx + 20) { swipeRef.current.active = false; return; }
    setTearProgress(Math.min(1, dx / 160));
  }, []);

  const onSwipeUp = useCallback(() => {
    if (!swipeRef.current.active) return;
    swipeRef.current.active = false;
    if (tearProgress >= 0.72) {
      setTearComplete(true);
      setTimeout(() => {
        setPickedCard(null);
        setCardFlipped(false);
        setPhase("spin");
        setTearProgress(0);
        setTearComplete(false);
      }, 700);
    } else {
      setTearProgress(0);
    }
  }, [tearProgress]);

  // ── Pick one card from carousel ───────────────────────────────────────────
  const handlePickCard = useCallback((card: LiveCard) => {
    setPickedCard(card);
    setCardFlipped(false);
    setPhase("reveal");
    // Trim the stored pack to only the card the user kept
    setCurrentPack((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, cards: [card], visualCards: [card] };
      setHistory((h) => h.map((p) => p.packId === prev.packId ? updated : p));
      return updated;
    });
    // After card mounts face-down, flip it with a delay for drama
    setTimeout(() => {
      setCardFlipped(true);
      if (card.rarity === "holo") {
        setFlashRarity("holo");
        setTimeout(() => setFlashRarity(null), 600);
        confetti({ particleCount: 40, spread: 100, origin: { x: 0.5, y: 0.6 },
          colors: ["#fbbf24", "#f59e0b", "#fde68a", "#ffffff"], scalar: 0.8, ticks: 150 });
      } else if (card.rarity === "ultra") {
        setFlashRarity("ultra");
        setTimeout(() => setFlashRarity(null), 800);
        confetti({ particleCount: 70, spread: 130, origin: { x: 0.5, y: 0.55 },
          colors: ["#c084fc", "#818cf8", "#e879f9", "#ffffff"], scalar: 1.0, ticks: 200 });
        setTimeout(() => setGrailCard(card), 650);
      } else if (card.rarity === "graded") {
        setFlashRarity("graded");
        setTimeout(() => setFlashRarity(null), 1000);
        confetti({ particleCount: 120, spread: 360, origin: { x: 0.5, y: 0.5 },
          colors: ["#ffd700", "#ffb700", "#fffacd", "#ffffff"], scalar: 1.2, ticks: 300 });
        setTimeout(() => setGrailCard(card), 650);
      }
    }, 750);
  }, []);

  // ── Open cashout modal ────────────────────────────────────────────────────
  const openCashout = useCallback((pack: DrawnPack, flow: CashoutFlow) => {
    if (DEMO_MODE || pack.demo) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    setCashoutPack(pack);
    setCashoutFlow(flow);
    setSellRefundToken(pack.payToken); // default to original payment token
    setSellConfirmStep("review");
    setShipStep("form");
    setShipForm({ name:"", line1:"", city:"", country:"", zip:"" });
  }, []);

  // ── Sell back ─────────────────────────────────────────────────────────────
  const handleSellBack = useCallback(async (pack: DrawnPack, refundToken = sellRefundToken) => {
    if (DEMO_MODE || pack.demo) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    setIsSelling(true);
    try {
      // Get card value and backend signature.
      // Cached in the pack record so we only call the API once per pack.
      let cardValueUsd18 = pack.cardValueUsd18;
      let sig            = pack.cardValueSig;

      if (!cardValueUsd18 || !sig) {
        // Derive card value from the pulled card's approxValueUsd
        const cardUsd  = pack.cards[0]?.approxValueUsd ?? 0;
        const valueRaw = BigInt(Math.round(cardUsd * 1e18)).toString();

        const API_BASE = import.meta.env.VITE_INDEXER_API ?? "";
        const resp = await fetch(`${API_BASE}/api/cards/sign-value`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ packId: pack.packId.toString(), cardValueUsd18: valueRaw }),
        });
        if (!resp.ok) throw new Error("Signing service unavailable — try again");
        const data = await resp.json();
        cardValueUsd18 = data.cardValueUsd18;
        sig            = data.signature;

        // Cache in localStorage so next sell attempt is instant
        setHistory((h) => h.map((p) =>
          p.packId === pack.packId ? { ...p, cardValueUsd18, cardValueSig: sig } : p
        ));
      }

      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "sellBack",
        args: [pack.packId, refundToken, BigInt(cardValueUsd18!), sig as `0x${string}`],
      });

      const cardUsd = pack.cards[0]?.approxValueUsd ?? 0;
      const bd = getSellBreakdown(pack, refundToken, cardUsd);
      helperToast.success(bd.isGem
        ? `Sold! ${bd.gemsBack.toFixed(0)} GEM minted to your wallet.`
        : `Sold! $${bd.tokenBack.toFixed(2)} in ${bd.tokenSymbol} returned.`);
      const upd = (p: DrawnPack) => p.packId === pack.packId ? { ...p, soldBack: true } : p;
      setHistory((h) => h.map(upd));
      setCurrentPack((c) => c ? upd(c) : c);
      setSellConfirmStep("done");
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? e?.message ?? "Sell-back failed");
    } finally {
      setIsSelling(false);
    }
  }, [writeContractAsync, sellRefundToken]);

  // ── Ship ──────────────────────────────────────────────────────────────────
  const handleShipConfirm = useCallback(async () => {
    if (!cashoutPack) return;
    if (DEMO_MODE || cashoutPack.demo) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    try {
      const infoBytes = `0x${Array.from(
        new TextEncoder().encode(JSON.stringify(shipForm))
      ).map((b) => b.toString(16).padStart(2,"0")).join("")}` as `0x${string}`;

      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "requestShipment",
        args: [cashoutPack.packId, infoBytes],
      });

      const trackRef = `BESC-${Date.now().toString(36).toUpperCase()}`;
      const upd = (p: DrawnPack) => p.packId === cashoutPack.packId
        ? { ...p, shipped: true, shipStatus: "pending" as const, trackingRef: trackRef }
        : p;
      setHistory((h) => h.map(upd));
      setCurrentPack((c) => c ? upd(c) : c);
      setShipStep("done");
      helperToast.success("Shipment confirmed! Cards on their way.");
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Shipment request failed");
    }
  }, [cashoutPack, shipForm, writeContractAsync]);

  // ── Claim referral earnings ────────────────────────────────────────────────
  const handleClaimRef = useCallback(async () => {
    if (DEMO_MODE) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    if (!account || claimable <= 0) return;
    setIsClaiming(true);
    try {
      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "claimReferralEarnings",
        args: [refClaimToken],
      });
      const sym = ["BUSDC","WBNB","WETH","BESC"][refClaimToken];
      helperToast.success(`$${claimable.toFixed(2)} claimed in ${sym}!`);
      setTimeout(() => refetchRefStats(), 3000);
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Claim failed");
    } finally {
      setIsClaiming(false);
    }
  }, [account, claimable, refClaimToken, writeContractAsync, refetchRefStats]);

  // ── GEM cashout ───────────────────────────────────────────────────────────
  const handleGemCashout = useCallback(async () => {
    if (DEMO_MODE) {
      helperToast.info(DEMO_BLOCKED_MSG);
      return;
    }
    if (!account) return;
    const amt = parseFloat(gemCashoutAmt);
    if (!amt || amt <= 0) return;
    setIsGemCashing(true);
    try {
      const gemAmountBig = BigInt(Math.floor(amt)) * 10n ** 18n;
      await writeContractAsync({
        address: SHOP_ADDRESS, abi: CARD_PACK_SHOP_ABI, functionName: "cashoutGems",
        args: [gemAmountBig, gemCashoutToken],
      });
      const sym = ["BUSDC","WBNB","WETH","BESC"][gemCashoutToken];
      helperToast.success(`${Math.floor(amt).toLocaleString()} GEM cashed out to ${sym}!`);
      setGemCashoutAmt("");
    } catch (e: any) {
      helperToast.error(e?.shortMessage ?? "Cashout failed");
    } finally {
      setIsGemCashing(false);
    }
  }, [account, gemCashoutAmt, gemCashoutToken, writeContractAsync]);

  // ── Copy ref link ─────────────────────────────────────────────────────────
  const copyRefLink = useCallback(() => {
    navigator.clipboard.writeText(refLink);
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 2000);
  }, [refLink]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const displayCards  = currentPack?.visualCards ?? [];
  const accentColor   = currentPack ? TIER_DATA[currentPack.tier].accent : "#6366f1";
  const shipFormValid = shipForm.name && shipForm.line1 && shipForm.city && shipForm.country && shipForm.zip;

  // ─── Cashout Modal ─────────────────────────────────────────────────────────
  const renderCashoutModal = () => {
    if (!cashoutPack || !cashoutFlow) return null;
    // Card the user picked — drives refund calculation
    const pickedInPack = cashoutPack.cards[0];
    const bd = getSellBreakdown(cashoutPack, sellRefundToken, pickedInPack?.approxValueUsd);

    return (
      <motion.div className="CPP__overlay"
        initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        onClick={() => { if (sellConfirmStep !== "done" && shipStep !== "done") { setCashoutPack(null); setCashoutFlow(null); } }}
      >
        <motion.div className="CPP__cashout-modal"
          initial={{scale:0.9,opacity:0,y:30}} animate={{scale:1,opacity:1,y:0}}
          exit={{scale:0.9,opacity:0}} transition={{type:"spring",stiffness:260,damping:24}}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── SELL FLOW ── */}
          {cashoutFlow === "sell" && sellConfirmStep === "review" && (
            <>
              <div className="CPP__co-header">
                <div className="CPP__co-icon sell">💰</div>
                <div>
                  <h2 className="CPP__co-title">Sell Back Pack</h2>
                  <p className="CPP__co-sub">{TIER_DATA[cashoutPack.tier].name} · {new Date(cashoutPack.timestamp).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Receive-in token selector */}
              <p className="CPP__co-section-label">Receive refund in</p>
              <div className="CPP__co-token-grid">
                {TOKENS.map((t) => {
                  const isGem = t.id === 4;
                  const active = sellRefundToken === t.id;
                  return (
                    <button key={t.id}
                      className={`CPP__co-token-opt${active ? " active" : ""}${isGem ? " gem-opt" : ""}`}
                      onClick={() => setSellRefundToken(t.id)}
                    >
                      <span className="CPP__co-token-icon">{t.icon}</span>
                      <span className="CPP__co-token-sym">{t.symbol}</span>
                      <span className={`CPP__co-token-pct${isGem ? " gem" : ""}`}>{isGem ? "75%" : "50%"}</span>
                    </button>
                  );
                })}
              </div>

              {/* Value breakdown */}
              <div className="CPP__co-breakdown">
                <div className="CPP__co-row">
                  <span>Card market value</span>
                  <span>${bd.cardUsd.toFixed(2)}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Return rate</span>
                  <span className="CPP__co-row-pct">{bd.isGem ? "75%" : "50%"} of card value</span>
                </div>
                <div className="CPP__co-divider" />
                <div className="CPP__co-row total">
                  <span>You receive</span>
                  {bd.isGem
                    ? <span className="gem-value">{bd.gemsBack.toFixed(0)} <span className="gem-icon">💎</span> GEM</span>
                    : <span className="token-value">{bd.tokenBack.toFixed(4)} {bd.tokenSymbol}</span>
                  }
                </div>
                <p className="CPP__co-note">
                  {bd.isGem
                    ? "GEM tokens are instantly minted to your wallet. Spend in-platform or swap for 25% bonus."
                    : `${bd.tokenSymbol} transferred on-chain within 1–2 blocks after confirmation.`}
                </p>
              </div>

              <div className="CPP__co-actions">
                <button className="CPBtn ghost" onClick={() => { setCashoutPack(null); setCashoutFlow(null); }}>Cancel</button>
                <button className="CPBtn sell" disabled={isSelling}
                  onClick={() => handleSellBack(cashoutPack, sellRefundToken)}>
                  {isSelling ? "Processing…" : `Confirm · Get ${bd.isGem ? `${bd.gemsBack.toFixed(0)} GEM` : `${bd.tokenBack.toFixed(4)} ${bd.tokenSymbol}`}`}
                </button>
              </div>
            </>
          )}

          {cashoutFlow === "sell" && sellConfirmStep === "done" && (
            <div className="CPP__co-done">
              <div className="CPP__co-done-icon">✅</div>
              <h2>Sold Successfully!</h2>
              <p className="CPP__co-sub">Your {bd.isGem ? "GEM tokens have been minted" : `${bd.tokenSymbol} refund is on-chain`}. Check your wallet.</p>
              <button className="CPBtn ghost" onClick={() => { setCashoutPack(null); setCashoutFlow(null); }}>Close</button>
            </div>
          )}

          {/* ── SHIP FLOW ── */}
          {cashoutFlow === "ship" && shipStep === "form" && (
            <>
              <div className="CPP__co-header">
                <div className="CPP__co-icon ship">📦</div>
                <div>
                  <h2 className="CPP__co-title">Ship Cards to Me</h2>
                  <p className="CPP__co-sub">Physical cards dispatched within 5–10 business days</p>
                </div>
              </div>

              <div className="CPP__co-ship-info">
                <div className="CPP__co-ship-badge free">🚚 Free shipping on packs over $25</div>
                <div className="CPP__co-ship-badge graded">🏆 Graded slabs in protective cases</div>
              </div>

              <div className="CPP__ship-form">
                {([ ["name","Full Name","John Smith"],["line1","Street Address","123 Main St"],
                     ["city","City","New York"],["country","Country","United States"],
                     ["zip","Postal Code","10001"] ] as const).map(([f,l,ph]) => (
                  <label key={f} className="CPP__ship-field">
                    <span>{l}</span>
                    <input value={shipForm[f as keyof typeof shipForm]}
                      onChange={(e) => setShipForm((p) => ({...p,[f]:e.target.value}))}
                      placeholder={ph} />
                  </label>
                ))}
              </div>

              <div className="CPP__co-actions">
                <button className="CPBtn ghost" onClick={() => { setCashoutPack(null); setCashoutFlow(null); }}>Cancel</button>
                <button className="CPBtn ship" disabled={!shipFormValid}
                  onClick={() => setShipStep("review")}>
                  Review Order →
                </button>
              </div>
            </>
          )}

          {cashoutFlow === "ship" && shipStep === "review" && (
            <>
              <div className="CPP__co-header">
                <div className="CPP__co-icon ship">📋</div>
                <div>
                  <h2 className="CPP__co-title">Confirm Shipment</h2>
                  <p className="CPP__co-sub">Review your shipping details</p>
                </div>
              </div>

              <div className="CPP__co-breakdown">
                <div className="CPP__co-row">
                  <span>Name</span><span>{shipForm.name}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Address</span><span>{shipForm.line1}</span>
                </div>
                <div className="CPP__co-row">
                  <span>City</span><span>{shipForm.city}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Country</span><span>{shipForm.country}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Postal code</span><span>{shipForm.zip}</span>
                </div>
                <div className="CPP__co-divider" />
                <div className="CPP__co-row">
                  <span>Pack</span><span>{TIER_DATA[cashoutPack.tier].name}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Cards</span><span>{cashoutPack.cards.length}</span>
                </div>
                <div className="CPP__co-row">
                  <span>Est. delivery</span><span>5–10 business days</span>
                </div>
                <div className="CPP__co-row total">
                  <span>Shipping cost</span>
                  <span className="token-value">
                    {Number(TIER_DATA[cashoutPack.tier].priceRaw[0]) / 1_000_000 >= 25 ? "Free 🎉" : "$4.99"}
                  </span>
                </div>
                <p className="CPP__co-note">Your shipping info is encrypted and only accessible by our fulfillment team.</p>
              </div>

              <div className="CPP__co-actions">
                <button className="CPBtn ghost" onClick={() => setShipStep("form")}>← Edit</button>
                <button className="CPBtn ship" onClick={handleShipConfirm}>Confirm Shipment</button>
              </div>
            </>
          )}

          {cashoutFlow === "ship" && shipStep === "done" && cashoutPack && (
            <div className="CPP__co-done">
              <div className="CPP__co-done-icon">📦</div>
              <h2>Shipment Confirmed!</h2>
              <div className="CPP__co-tracking">
                <span className="CPP__co-tracking-label">Tracking Reference</span>
                <span className="CPP__co-tracking-code">
                  {history.find(p => p.packId === cashoutPack.packId)?.trackingRef ?? "—"}
                </span>
              </div>
              <p className="CPP__co-sub">Expected delivery: 5–10 business days. You'll receive a confirmation email.</p>
              <button className="CPBtn ghost" onClick={() => { setCashoutPack(null); setCashoutFlow(null); }}>Close</button>
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="CPP">

      {/* ── Rarity flash overlay ── */}
      <AnimatePresence>
        {flashRarity && (
          <motion.div className={`CPP__flash CPP__flash--${flashRarity}`}
            initial={{ opacity: 0 }} animate={{ opacity: [0, 0.55, 0] }}
            exit={{ opacity: 0 }} transition={{ duration: 0.55 }}
          />
        )}
      </AnimatePresence>

      {/* ── Grail overlay ── */}
      <GrailScreen card={grailCard} onClose={() => setGrailCard(null)} />

      {/* ── Card detail — full-screen SlabView ── */}
      <AnimatePresence>
        {detailCard && (
          <SlabView card={detailCard} onClose={() => setDetailCard(null)} />
        )}
      </AnimatePresence>

      {/* ── Cash-out modal ── */}
      <AnimatePresence>
        {cashoutPack && cashoutFlow && renderCashoutModal()}
      </AnimatePresence>

      {/* ══════════ BANNER ══════════ */}
      <div className="CPP__banner">
        <div className="CPP__hero-bg" />
        <div className="CPP__banner-inner">
          <motion.div className="CPP__hero-content" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{duration:0.5}}>
            {DEMO_MODE ? (
              <div className="CPP__hero-badge CPP__hero-badge--demo"><span className="dot-amber" /> Demo · Coming soon to BESC Hyperchain</div>
            ) : (
              <div className="CPP__hero-badge"><span className="dot-green" /> Live on BESC Hyperchain</div>
            )}
            <h1 className="CPP__hero-title">BESC<span className="gradient-text"> Draw</span></h1>
            <p className="CPP__hero-sub">
              {DEMO_MODE
                ? "Try it free · Demo packs are simulated and have no cash value"
                : "Real Pokémon TCG cards · Open packs · Sell back for crypto"}
            </p>
            {DEMO_MODE && (
              <div className="CPP__demo-notice">
                Demo mode: packs are free and nothing is charged. Selling, shipping and cash-outs open at launch.
                Bought packs before the demo? Contact support for a refund.
              </div>
            )}
            <div className="CPP__hero-trust">
              {["🔒 Non-custodial","♻️ 7-day sell-back","⚔️ Battles PvP","📦 Worldwide shipping"].map((t) => (
                <span key={t} className="CPP__hero-trust-item">{t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <LiveTicker />

      {/* ══════════ MAIN ══════════ */}
      <div className="CPP__main">
      <div className="CPP__body">

        {/* Desktop tabs */}
        <div className="CPP__tabs-row">
        {account && gemBalance != null && (gemBalance as bigint) > 0n && (
          <div className="CPP__gem-chip">
            <span className="CPP__gem-chip-icon">💎</span>
            {Number((gemBalance as bigint) / 10n ** 18n).toLocaleString()} GEM
          </div>
        )}
        <div className="CPP__tabs">
          {([
            { id: "select",     label: "Open Packs",  icon: "🎴" },
            { id: "battles",    label: "Battles",     icon: "⚔️" },
            { id: "history",    label: `My Packs${history.length ? ` (${history.length})` : ""}`, icon: "📦" },
            { id: "collection", label: `Collection${allCollCards.length ? ` (${allCollCards.length})` : ""}`, icon: "🃏" },
            { id: "cashout",    label: "Cash Out",    icon: "💰" },
            { id: "referral",   label: "Refer & Earn",icon: "🔗" },
          ] as const).map((t) => {
            const isActive =
              (["select","opening","tearing","spin","reveal","batch"].includes(phase) && t.id === "select") ||
              (phase === t.id);
            return (
              <button key={t.id}
                className={`CPP__tab${isActive ? " active" : ""}${t.id === "referral" && claimable > 0 ? " has-badge" : ""}`}
                onClick={() => {
                  if (t.id === "select") {
                    if (!["opening","tearing","spin","reveal"].includes(phase)) setPhase("select");
                  } else {
                    setPhase(t.id as Phase);
                  }
                }}
              >
                {t.label}
                {t.id === "referral" && claimable > 0 && (
                  <span className="CPP__tab-badge">${claimable.toFixed(2)}</span>
                )}
              </button>
            );
          })}
        </div>{/* CPP__tabs */}
        </div>{/* CPP__tabs-row */}

        <AnimatePresence mode="wait">

          {/* ════ BATTLES ════ */}
          {phase === "battles" && !activeBattle && (
            <motion.div key="battles" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <BattlesTab
                account={account}
                onEnterBattle={(battle) => setActiveBattle(battle)}
              />
            </motion.div>
          )}

          {/* ════ SELECT ════ */}
          {phase === "select" && (
            <motion.div key="select" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <PackSelector
                demo={DEMO_MODE}
                selectedTier={selectedTier} selectedToken={selectedToken} tcg={tcg}
                gemBalance={(gemBalance as bigint|undefined) ?? 0n}
                packPrices={effectivePackPrices}
                usdPrice={usdPrice}
                onTierChange={setSelectedTier} onTokenChange={setSelectedToken}
                onOpen={handleOpenPack} isOpening={isOpening}
                needsApproval={needsApproval} hasBalance={hasBalance}
                isApproving={isApproving} onApprove={handleApprove}
                account={account}
                volatility={volatility} onVolatilityChange={setVolatility}
                packCount={Math.min(packCount, maxBatchSize)}
                onPackCountChange={setPackCount}
                giftMode={giftMode} onGiftModeChange={setGiftMode}
                giftRecipient={giftRecipient} onGiftRecipientChange={setGiftRecipient}
                promoCode={promoCode} onPromoCodeChange={setPromoCode}
                promoDiscount={promoDiscount}
                tierSoldOut={tierSoldOut}
                tierSupplyUsed={tierSupplyUseds[selectedTier]}
                tierMaxSupply={tierMaxSupplies[selectedTier]}
                jackpotInterval={jackpotInterval}
                packCount_global={globalPackCount}
              />
            </motion.div>
          )}

          {/* ════ OPENING ════ */}
          {phase === "opening" && (
            <motion.div key="opening" className="CPP__opening"
              initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            >
              {/* Ambient glow bg */}
              <div className="CPP__opening-bg" style={{
                background: `radial-gradient(ellipse 60% 60% at 50% 50%, ${tierData.accent}22 0%, transparent 70%)`
              }} />

              {/* Outer energy rings */}
              {[1,2,3].map((i) => (
                <motion.div key={i} className="CPP__opening-energy-ring"
                  style={{ borderColor: `${tierData.accent}${['44','28','14'][i-1]}`, width: `${160 + i*90}px`, height: `${160 + i*90}px` }}
                  animate={{ scale:[1, 1.08+i*0.04, 1], opacity:[0.4, 0.8, 0.4] }}
                  transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.3, ease:"easeInOut" }}
                />
              ))}

              {/* Pack + glow */}
              <div className="CPP__opening-pack-wrap">
                <motion.div className="CPP__opening-glow"
                  animate={{ opacity:[0.4,1,0.4], scale:[0.95,1.1,0.95] }}
                  transition={{ duration:1.1, repeat:Infinity }}
                  style={{ background: `radial-gradient(circle, ${tierData.accent}55 0%, transparent 65%)` }}
                />
                <motion.div
                  className="CPP__opening-pack"
                  animate={{ rotate:[-4,4,-4,2,-2,4,-4], scale:[1,1.04,1,1.06,1] }}
                  transition={{ duration:0.7, repeat:Infinity, ease:"easeInOut" }}
                  style={{ background:`linear-gradient(160deg, ${tierData.bgTop}, ${tierData.bgMid})`,
                           borderColor: tierData.stripeColor,
                           boxShadow: `0 0 40px ${tierData.accent}55, 0 20px 60px rgba(0,0,0,0.5)` }}
                >
                  <span style={{fontSize:"3.2rem"}}>{tierData.emoji}</span>
                  <div className="CPP__opening-label" style={{color:tierData.stripeColor}}>{tierData.name}</div>
                </motion.div>
              </div>

              {/* Status text */}
              <div className="CPP__opening-status">
                <motion.div className="CPP__opening-dots">
                  {[0,1,2].map(i => (
                    <motion.span key={i} className="CPP__opening-dot"
                      style={{ background: tierData.accent }}
                      animate={{ scale:[1,1.5,1], opacity:[0.4,1,0.4] }}
                      transition={{ duration:0.8, repeat:Infinity, delay:i*0.18 }}
                    />
                  ))}
                </motion.div>
                <motion.p className="CPP__opening-hint"
                  animate={{opacity:[0.6,1,0.6]}} transition={{duration:1.8,repeat:Infinity}}
                >
                  Confirming on BESC Hyperchain…
                </motion.p>
                <p className="CPP__opening-sub">Do not close this tab</p>
              </div>
            </motion.div>
          )}

          {/* ════ TEARING ════ */}
          {phase === "tearing" && currentPack && (
            <motion.div key="tearing" className="CPP__tearing"
              initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0,scale:0.9}}
            >
              <motion.div className="CPP__tear-ambient"
                animate={{ opacity: tearProgress * 0.75, scale: 0.6 + tearProgress * 0.6 }}
                style={{ background: `radial-gradient(circle, ${accentColor}50 0%, transparent 65%)` }}
                transition={{ duration: 0.05 }}
              />

              <motion.div className="CPP__swipe-hint"
                animate={{ opacity: tearProgress > 0.08 ? 0 : 1, y: tearProgress > 0.08 ? -10 : 0 }}
                transition={{ duration: 0.15 }}
              >
                <motion.span animate={{ x: [-8, 8, -8] }} transition={{ repeat: Infinity, duration: 0.85, ease: "easeInOut" }}>←</motion.span>
                <span>Slide across the top to tear</span>
                <motion.span animate={{ x: [8, -8, 8] }} transition={{ repeat: Infinity, duration: 0.85, ease: "easeInOut" }}>→</motion.span>
              </motion.div>

              <motion.div
                className="CPP__tear-pack-wrap"
                animate={tearComplete
                  ? { y: -480, rotate: -20, scale: 0.5, opacity: 0 }
                  : { rotate: tearProgress * 5 }}
                transition={tearComplete
                  ? { duration: 0.55, ease: [0.4, 0, 1, 1] }
                  : { duration: 0.04 }}
                style={{ touchAction: "pan-y" }}
                onPointerDown={onSwipeDown}
                onPointerMove={onSwipeMove}
                onPointerUp={onSwipeUp}
                onPointerCancel={onSwipeUp}
              >
                <div className="CPP__tear-line-wrap">
                  <div className="CPP__tear-perforation" />
                  <motion.div
                    className="CPP__tear-line"
                    animate={{ width: `${tearProgress * 100}%`, opacity: tearProgress > 0.02 ? 1 : 0 }}
                    style={{ '--tear-glow': accentColor, background: `linear-gradient(90deg, ${accentColor}bb, #fff 55%, ${accentColor})` } as React.CSSProperties}
                    transition={{ duration: 0.04 }}
                  />
                  {tearProgress > 0.04 && (
                    <div
                      className="CPP__tear-sparks"
                      style={{ left: `calc(${tearProgress * 100}% - 2px)`, '--spark-color': accentColor } as React.CSSProperties}
                    >
                      {[0,1,2,3,4,5,6,7,8,9].map((i) => (
                        <motion.span key={i} className="CPP__tear-spark"
                          style={{ width: `${3 + (i % 3) * 2}px`, height: `${3 + (i % 3) * 2}px` } as React.CSSProperties}
                          animate={{ y: [0, -(12 + (i % 4) * 10)], x: [(i - 4.5) * 5, (i - 4.5) * 11], opacity: [1, 0], scale: [1, 0.2] }}
                          transition={{ duration: 0.42 + (i % 3) * 0.12, delay: (i % 5) * 0.04, repeat: Infinity }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <Pack3D tier={TIER_DATA[currentPack.tier]} active />
              </motion.div>

              <div className="CPP__tear-progress-row">
                <div className="CPP__tear-bar">
                  <motion.div
                    className="CPP__tear-bar-fill"
                    animate={{ width: `${tearProgress * 100}%` }}
                    transition={{ duration: 0.04 }}
                    style={{ background: `linear-gradient(90deg, ${accentColor}88, ${accentColor})` }}
                  />
                </div>
              </div>

              {tearProgress >= 0.75 && !tearComplete && (
                <motion.p className="CPP__tear-release"
                  initial={{ opacity: 0, scale: 0.75 }}
                  animate={{ opacity: [0.7, 1, 0.7], scale: [0.96, 1.08, 0.96] }}
                  transition={{ duration: 0.38, repeat: Infinity }}
                  style={{ color: accentColor }}
                >
                  🔥 RELEASE!
                </motion.p>
              )}
            </motion.div>
          )}

          {/* ════ SPIN CAROUSEL — pick one card blind ════ */}
          {phase === "spin" && currentPack && (
            <motion.div key="spin" className="CPP__spin-phase"
              initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-20}}
            >
              <div className="CPP__spin-header">
                <h2 className="CPP__spin-title">
                  <span style={{color: TIER_DATA[currentPack.tier].accent}}>{TIER_DATA[currentPack.tier].emoji}</span>
                  {" "}Pick Your Card
                </h2>
                <p className="CPP__spin-sub">
                  {displayCards.length} cards in the rack · all face-down · you get one
                </p>
              </div>

              <div className="CPP__spin-hint-row">
                <motion.span animate={{x:[-8,8,-8]}} transition={{repeat:Infinity,duration:1.1}}>←</motion.span>
                <span>Swipe to browse · Tap to rip</span>
                <motion.span animate={{x:[8,-8,8]}} transition={{repeat:Infinity,duration:1.1}}>→</motion.span>
              </div>

              <div className="CPP__spin-viewport" ref={spinViewportRef}>
                <motion.div
                  className="CPP__spin-track"
                  drag="x"
                  dragConstraints={spinViewportRef}
                  dragElastic={0.12}
                  dragTransition={{ bounceStiffness: 180, bounceDamping: 28 }}
                >
                  {displayCards.map((card, idx) => (
                    <motion.div key={idx} className="CPP__spin-slot"
                      whileHover={{ y: -32, scale: 1.1, zIndex: 20 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type:"spring", stiffness:340, damping:24 }}
                      onClick={() => handlePickCard(card)}
                    >
                      <PokemonCard card={card} faceDown revealed={false} index={idx} size="md" />
                    </motion.div>
                  ))}
                </motion.div>
              </div>

              <p className="CPP__spin-footer-note">
                🔒 All cards sealed on-chain · Pick any — your fate is already written
              </p>
            </motion.div>
          )}

          {/* ════ SINGLE CARD REVEAL ════ */}
          {phase === "reveal" && currentPack && pickedCard && (
            <motion.div key="reveal" className="CPP__single-reveal"
              initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            >
              {/* Ambient glow behind the card */}
              <motion.div className="CPP__single-reveal-glow"
                initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}}
                transition={{delay:0.6,duration:0.8}}
                style={{ background: `radial-gradient(circle, ${RARITY_COLOR[pickedCard.rarity]}55 0%, transparent 65%)` }}
              />

              <div className="CPP__single-reveal-card">
                <PokemonCard card={pickedCard} faceDown={true} revealed={cardFlipped} index={0} size="lg" />
              </div>

              <AnimatePresence>
                {cardFlipped && (
                  <motion.div className="CPP__single-reveal-info"
                    initial={{opacity:0,y:24}} animate={{opacity:1,y:0}}
                    exit={{opacity:0}} transition={{delay:0.5,type:"spring",stiffness:260,damping:22}}
                  >
                    <h2 className="CPP__single-reveal-name">{pickedCard.name}</h2>
                    <p className="CPP__single-reveal-set">{pickedCard.setName} · #{pickedCard.number}</p>
                    <div className="CPP__single-reveal-rarity" style={{color:RARITY_COLOR[pickedCard.rarity]}}>
                      {pickedCard.rarity.charAt(0).toUpperCase()+pickedCard.rarity.slice(1)}
                      {pickedCard.isAltArt && " · Alt Art"}
                      {pickedCard.isGraded && pickedCard.grade != null && ` · ${pickedCard.gradingCompany} ${pickedCard.grade}`}
                    </div>
                    <div className="CPP__single-reveal-value">
                      ~${pickedCard.approxValueUsd.toLocaleString()}
                    </div>
                    <div className="CPP__single-reveal-acts">
                      {!currentPack.soldBack && !currentPack.shipped && (
                        <>
                          <motion.button className="CPBtn sell"
                            onClick={() => openCashout(currentPack, "sell")}
                            whileHover={{scale:1.04}} whileTap={{scale:0.97}}>
                            💰 Sell Back
                          </motion.button>
                          <motion.button className="CPBtn ship"
                            onClick={() => openCashout(currentPack, "ship")}
                            whileHover={{scale:1.04}} whileTap={{scale:0.97}}>
                            📦 Ship to Me
                          </motion.button>
                          <motion.button className="CPBtn hold"
                            onClick={() => { helperToast.success("Card saved to your collection!"); setPhase("history"); }}
                            whileHover={{scale:1.04}} whileTap={{scale:0.97}}>
                            🗄️ Hold for Now
                          </motion.button>
                        </>
                      )}
                      {(currentPack.soldBack || currentPack.shipped) && (
                        <span className="CPBadge redeemed">{currentPack.soldBack ? "Sold Back ✓" : "Shipped ✓"}</span>
                      )}
                      <motion.button className="CPBtn ghost"
                        whileHover={{scale:1.04}} whileTap={{scale:0.97}}
                        onClick={() => {
                          const txt = `🎴 Just pulled ${pickedCard.name} (${pickedCard.rarity}) from a ${TIER_DATA[currentPack.tier].name} on BESC Draw! Worth ~$${pickedCard.approxValueUsd}! 🔥`;
                          if (navigator.share) navigator.share({ title:"BESC Draw Pull", text: txt }).catch(()=>{});
                          else navigator.clipboard.writeText(txt).then(() => helperToast.success("Copied!"));
                        }}
                      >Share Pull</motion.button>
                      <motion.button className="CPBtn new"
                        whileHover={{scale:1.04}} whileTap={{scale:0.97}}
                        onClick={() => setPhase("select")}>
                        Open Another
                      </motion.button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* ════ BATCH REVEAL ════ */}
          {phase === "batch" && (
            <motion.div key="batch" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
              <div className="CPP__batch-header">
                <div>
                  <h2 className="CPP__batch-title">
                    🎉 {batchPacks.length} Packs Opened!
                  </h2>
                  <p className="CPP__batch-sub">
                    Total value: <strong className="green">
                      ${batchPacks.flatMap(p=>p.cards).reduce((s,c)=>s+c.approxValueUsd,0).toLocaleString(undefined,{maximumFractionDigits:0})}
                    </strong> · {batchPacks.flatMap(p=>p.cards).length} cards
                  </p>
                </div>
                <div style={{display:"flex",gap:"0.5rem"}}>
                  <button className="CPBtn ghost" onClick={() => setPhase("history")}>View All Packs</button>
                  <button className="CPBtn new" onClick={() => setPhase("select")}>Open More</button>
                </div>
              </div>

              <div className="CPP__batch-grid">
                {batchPacks.map((pack, pi) => {
                  const pv = pack.cards.reduce((s,c)=>s+c.approxValueUsd,0);
                  const tierAccent = TIER_DATA[pack.tier].accent;
                  const topCards = [...pack.cards].sort((a,b)=>b.approxValueUsd-a.approxValueUsd).slice(0,3);
                  return (
                    <motion.div
                      key={pi}
                      className="CPP__batch-pack-card"
                      initial={{scale:0.85,opacity:0,y:20}}
                      animate={{scale:1,opacity:1,y:0}}
                      transition={{delay:pi*0.06,type:"spring",stiffness:260,damping:26}}
                      style={{borderColor:`${tierAccent}30`}}
                    >
                      <div className="CPP__batch-pack-header" style={{borderBottomColor:`${tierAccent}20`}}>
                        <span style={{color:tierAccent,fontWeight:700}}>{TIER_DATA[pack.tier].name}</span>
                        <span className="green" style={{fontWeight:800}}>${pv.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                      </div>
                      <div className="CPP__batch-pack-cards">
                        {topCards.map((card, ci) => (
                          <div key={ci} className="CPP__batch-mini-card" onClick={() => { setCurrentPack(pack); setPickedCard(null); setCardFlipped(false); setPhase("spin"); }}>
                            <img src={card.imageLarge ?? card.imageSmall} alt={card.name}
                              onError={(e)=>{const i=e.currentTarget as HTMLImageElement;if(card.imageSmall&&i.src!==card.imageSmall)i.src=card.imageSmall;else i.style.display="none";}} />
                          </div>
                        ))}
                      </div>
                      <div className="CPP__batch-pack-footer">
                        <span style={{color:"#6b7099",fontSize:"0.75rem"}}>{pack.cards.length} cards</span>
                        <button className="CPBtn ghost sm" onClick={()=>{ setCurrentPack(pack); setPickedCard(null); setCardFlipped(false); setPhase("spin"); }}>
                          Pick Card →
                        </button>
                      </div>
                      {!pack.soldBack && !pack.shipped && (
                        <div className="CPP__batch-pack-acts">
                          <button className="CPBtn sell sm" onClick={() => openCashout(pack, "sell")}>💰 Sell</button>
                          <button className="CPBtn ship sm" onClick={() => openCashout(pack, "ship")}>📦 Ship</button>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* ════ HISTORY ════ */}
          {phase === "history" && (
            <motion.div key="history" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              {history.length === 0 ? (
                <div className="CPP__empty">
                  <span className="CPP__empty-icon">🃏</span>
                  <p>No packs opened yet.</p>
                  <button className="CPBtn new" onClick={() => setPhase("select")}>Open Your First Pack</button>
                </div>
              ) : (
                <>
                  <div className="CPP__hstats">
                    {[
                      [history.length, "Packs Opened"],
                      [history.reduce((s,p) => s+p.cards.length, 0), "Cards Pulled"],
                      [`$${history.flatMap((p)=>p.cards).reduce((s,c)=>s+c.approxValueUsd,0).toLocaleString(undefined,{maximumFractionDigits:0})}`, "Total Value"],
                      [history.flatMap((p)=>p.cards).filter((c)=>c.rarity==="ultra"||c.rarity==="graded").length, "Ultra / Graded"],
                    ].map(([n, l]) => (
                      <div key={String(l)} className="CPP__hstat">
                        <span className="n">{n}</span>
                        <span className="l">{l}</span>
                      </div>
                    ))}
                  </div>

                  {history.map((pack, pi) => {
                    const pv = pack.cards.reduce((s,c)=>s+c.approxValueUsd,0);
                    const tierAccent = TIER_DATA[pack.tier].accent;
                    return (
                      <motion.div key={pi} className="CPP__hist-pack"
                        initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:pi*0.04}}
                        style={{ borderColor: `${tierAccent}30` }}
                      >
                        <div className="CPP__hist-pack-head">
                          <div className="CPP__hist-pack-meta">
                            <span className="tier" style={{color:tierAccent}}>{TIER_DATA[pack.tier].name}</span>
                            <span className="token">{TOKEN_SYMBOLS[pack.payToken]}</span>
                            <span className="value green">${pv.toLocaleString(undefined,{maximumFractionDigits:2})}</span>
                            {pack.shipped && pack.trackingRef && (
                              <span className="CPP__hist-tracking">
                                <span className="CPP__hist-track-dot" />
                                Ref: {pack.trackingRef}
                              </span>
                            )}
                            <span className="date">{new Date(pack.timestamp).toLocaleString()}</span>
                          </div>
                          <div className="CPP__hist-pack-acts">
                            {!pack.soldBack && !pack.shipped && (
                              <>
                                <button className="CPBtn sell sm" onClick={() => openCashout(pack, "sell")}>
                                  💰 Sell
                                </button>
                                <button className="CPBtn ship sm" onClick={() => openCashout(pack, "ship")}>
                                  📦 Ship
                                </button>
                              </>
                            )}
                            {pack.soldBack && <span className="CPBadge redeemed">Sold ✓</span>}
                            {pack.shipped  && (
                              <span className="CPBadge shipped">
                                {pack.shipStatus === "delivered" ? "Delivered ✓" :
                                 pack.shipStatus === "shipped"   ? "📬 In Transit" :
                                 pack.shipStatus === "processing"? "⚙️ Processing" : "📦 Pending"}
                              </span>
                            )}
                            <button className="CPBtn ghost sm" onClick={() => {
                              setCurrentPack(pack);
                              setPickedCard(null);
                              setCardFlipped(false);
                              setPhase("spin");
                            }}>Open Again</button>
                          </div>
                        </div>
                        <div className="CPP__hist-cards">
                          {pack.visualCards.map((card, ci) => (
                            <PokemonCard key={ci} card={card} index={ci} revealed size="sm"
                              onClick={() => setDetailCard(card)} />
                          ))}
                        </div>
                      </motion.div>
                    );
                  })}
                </>
              )}
            </motion.div>
          )}

          {/* ════ COLLECTION ════ */}
          {phase === "collection" && (
            <motion.div key="collection" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              {history.length === 0 ? (
                <div className="CPP__empty">
                  <span className="CPP__empty-icon">🗂️</span>
                  <p>Open your first pack to start your collection.</p>
                  <button className="CPBtn new" onClick={() => setPhase("select")}>Open a Pack</button>
                </div>
              ) : (
                <>
                  {/* Collection stats */}
                  <div className="CPP__hstats" style={{marginBottom:"1.5rem"}}>
                    {([
                      [allCollCards.length, "Total Cards"],
                      [`$${collTotalValue.toLocaleString(undefined,{maximumFractionDigits:0})}`, "Est. Value"],
                      [collGraded + collUltra, "Hits"],
                      [collAltArt, "Alt Arts"],
                    ] as [any,string][]).map(([n,l]) => (
                      <div key={l} className="CPP__hstat"><span className="n">{n}</span><span className="l">{l}</span></div>
                    ))}
                  </div>

                  {/* Filter / Sort / Search bar */}
                  <div className="CPP__coll-toolbar">
                    <div className="CPP__coll-search-wrap">
                      <span className="CPP__coll-search-icon">🔍</span>
                      <input
                        className="CPP__coll-search"
                        placeholder="Search cards…"
                        value={collSearch}
                        onChange={(e) => setCollSearch(e.target.value)}
                      />
                      {collSearch && (
                        <button className="CPP__coll-search-clear" onClick={() => setCollSearch("")}>×</button>
                      )}
                    </div>

                    <select className="CPP__coll-select" value={collRarity}
                      onChange={(e) => setCollRarity(e.target.value)}>
                      <option value="all">All Rarities</option>
                      <option value="graded">🏆 Graded</option>
                      <option value="ultra">💎 Ultra Rare</option>
                      <option value="holo">✨ Holo</option>
                      <option value="rare">⭐ Rare</option>
                      <option value="uncommon">🔵 Uncommon</option>
                      <option value="common">⬜ Common</option>
                    </select>

                    <select className="CPP__coll-select" value={collSort}
                      onChange={(e) => setCollSort(e.target.value as any)}>
                      <option value="value-desc">Value: High → Low</option>
                      <option value="value-asc">Value: Low → High</option>
                      <option value="rarity">By Rarity</option>
                      <option value="name">A → Z</option>
                    </select>

                    <div className="CPP__coll-view-toggle">
                      <button className={`CPP__coll-view-btn${collView==="grid"?" active":""}`}
                        onClick={() => setCollView("grid")}>⊞</button>
                      <button className={`CPP__coll-view-btn${collView==="list"?" active":""}`}
                        onClick={() => setCollView("list")}>☰</button>
                    </div>
                  </div>

                  {allCollCards.length === 0 && (
                    <div className="CPP__empty" style={{padding:"3rem 1rem"}}>
                      <span className="CPP__empty-icon" style={{fontSize:"2.5rem"}}>🔍</span>
                      <p>No cards match your filters.</p>
                    </div>
                  )}

                  {collView === "grid" ? (
                    <div className="CPP__card-grid">
                      {allCollCards.map((card, ci) => (
                        <motion.div key={`${card.id}-${ci}`}
                          initial={{scale:0.85,opacity:0}} animate={{scale:1,opacity:1}}
                          transition={{delay:Math.min(ci*0.015, 0.3),type:"spring",stiffness:260,damping:26}}
                          className="CPP__coll-card-wrap"
                        >
                          <PokemonCard card={card} index={ci} revealed size="md"
                            onClick={() => setDetailCard(card)} />
                          <div className="CPP__coll-card-val"
                            style={{color: RARITY_COLOR[card.rarity] ?? "#6b7099"}}>
                            ${card.approxValueUsd >= 1000
                              ? (card.approxValueUsd/1000).toFixed(1) + "k"
                              : card.approxValueUsd.toLocaleString()}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="CPP__coll-list">
                      {allCollCards.map((card, ci) => (
                        <motion.div key={`${card.id}-${ci}`}
                          className="CPP__coll-list-row"
                          initial={{opacity:0,x:-12}} animate={{opacity:1,x:0}}
                          transition={{delay:Math.min(ci*0.012, 0.25)}}
                          onClick={() => setDetailCard(card)}
                        >
                          <div className="CPP__coll-list-img">
                            <img
                              src={card.imageLarge ?? card.imageSmall}
                              alt={card.name}
                              onError={(e) => {
                                const img = e.currentTarget as HTMLImageElement;
                                if (card.imageSmall && img.src !== card.imageSmall) img.src = card.imageSmall;
                                else img.style.display = "none";
                              }}
                            />
                          </div>
                          <div className="CPP__coll-list-info">
                            <span className="CPP__coll-list-name">{card.name}</span>
                            <span className="CPP__coll-list-set">{card.setName}</span>
                          </div>
                          <span className="CPP__coll-list-rarity"
                            style={{color: RARITY_COLOR[card.rarity]}}>
                            {card.rarity.charAt(0).toUpperCase() + card.rarity.slice(1)}
                            {card.isGraded && card.grade != null && ` · ${card.gradingCompany} ${card.grade}`}
                          </span>
                          <span className="CPP__coll-list-val green">
                            ${card.approxValueUsd.toLocaleString()}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* ════ CASHOUT ════ */}
          {phase === "cashout" && (
            <motion.div key="cashout" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className="CPP__cashout-page">
                <div className="CPP__cashout-hero">
                  <h2 className="CPP__cashout-title">Cash Out Your Cards</h2>
                  <p className="CPP__cashout-sub">
                    Choose to sell your pack back for instant crypto, or ship the physical cards to your door.
                  </p>
                </div>

                {/* Option cards */}
                <div className="CPP__cashout-options">
                  <motion.div className="CPP__cashout-option sell"
                    whileHover={{scale:1.02,y:-2}} whileTap={{scale:0.98}}>
                    <div className="CPP__cashout-opt-icon">💰</div>
                    <h3>Sell Back</h3>
                    <p>Get crypto instantly. Choose between 50% in your original token or 75% in GEM.</p>
                    <ul className="CPP__cashout-opt-feats">
                      <li>⚡ Instant — 1–2 blocks</li>
                      <li>💵 50% in original token</li>
                      <li>💎 75% in GEM tokens</li>
                      <li>♻️ Valid within 48 hours</li>
                    </ul>
                  </motion.div>

                  <motion.div className="CPP__cashout-option ship"
                    whileHover={{scale:1.02,y:-2}} whileTap={{scale:0.98}}>
                    <div className="CPP__cashout-opt-icon">📦</div>
                    <h3>Ship to Me</h3>
                    <p>Get the physical cards delivered worldwide. PSA / BGS slabs in protective cases.</p>
                    <ul className="CPP__cashout-opt-feats">
                      <li>🌍 Worldwide shipping</li>
                      <li>🚚 Free over $25</li>
                      <li>🏆 Graded slabs protected</li>
                      <li>📬 5–10 business days</li>
                    </ul>
                  </motion.div>
                </div>

                {/* GEM Cashout */}
                {account && gemBalance != null && (gemBalance as bigint) > 0n && (
                  <div className="CPP__gem-cashout-card">
                    <div className="CPP__gem-cashout-header">
                      <span className="CPP__gem-cashout-icon">💎</span>
                      <div className="CPP__gem-cashout-hd">
                        <h3 className="CPP__gem-cashout-title">Cash Out GEM Tokens</h3>
                        <p className="CPP__gem-cashout-sub">Convert GEM to crypto · 100 GEM = $1 · 5% platform fee</p>
                      </div>
                      <span className="CPP__gem-cashout-bal">
                        {(Number(gemBalance as bigint) / 1e18).toLocaleString(undefined, {maximumFractionDigits: 0})} GEM
                      </span>
                    </div>
                    <div className="CPP__gem-cashout-form">
                      <div className="CPP__gem-cashout-input-row">
                        <input
                          className="CPP__gem-cashout-input"
                          type="number"
                          placeholder="Amount of GEM to cash out"
                          min="1"
                          value={gemCashoutAmt}
                          onChange={e => setGemCashoutAmt(e.target.value)}
                        />
                        <button className="CPP__gem-cashout-max"
                          onClick={() => setGemCashoutAmt(Math.floor(Number(gemBalance as bigint) / 1e18).toString())}
                        >MAX</button>
                      </div>
                      <div className="CPP__ref-claim-token-opts" style={{marginTop:"0.75rem"}}>
                        {TOKENS.filter(t => t.id <= 3).map(t => (
                          <button key={t.id}
                            className={`CPP__ref-claim-token-opt${gemCashoutToken === t.id ? " active" : ""}`}
                            onClick={() => setGemCashoutToken(t.id)}
                          >
                            <span>{t.icon}</span>
                            <span>{t.symbol}</span>
                          </button>
                        ))}
                      </div>
                      {gemCashoutAmt && parseFloat(gemCashoutAmt) > 0 && (
                        <p className="CPP__gem-cashout-preview">
                          ≈ ${(parseFloat(gemCashoutAmt) * 0.01 * 0.95).toFixed(2)} USD after 5% fee
                        </p>
                      )}
                      <motion.button
                        className={`CPP__gem-cashout-btn${parseFloat(gemCashoutAmt||"0") > 0 ? " ready" : ""}`}
                        disabled={!gemCashoutAmt || parseFloat(gemCashoutAmt) <= 0 || isGemCashing}
                        onClick={handleGemCashout}
                        whileHover={parseFloat(gemCashoutAmt||"0") > 0 ? {scale:1.02} : {}}
                        whileTap={parseFloat(gemCashoutAmt||"0") > 0 ? {scale:0.98} : {}}
                      >
                        {isGemCashing
                          ? "Processing…"
                          : `💎 Cash Out ${gemCashoutAmt || "0"} GEM → ${["BUSDC","WBNB","WETH","BESC"][gemCashoutToken]}`}
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Redeemable packs */}
                <div className="CPP__cashout-packs">
                  <h3 className="CPP__cashout-packs-title">Your Packs</h3>
                  {history.filter(p => !p.soldBack && !p.shipped).length === 0 ? (
                    <div className="CPP__empty" style={{padding:"3rem"}}>
                      <span className="CPP__empty-icon" style={{fontSize:"3rem"}}>✅</span>
                      <p>All packs have been redeemed. Open more packs to get started.</p>
                      <button className="CPBtn new" onClick={() => setPhase("select")}>Open a Pack</button>
                    </div>
                  ) : (
                    history.filter(p => !p.soldBack && !p.shipped).map((pack, pi) => {
                      const pv = pack.cards[0]?.approxValueUsd ?? 0; // card the user kept
                      const bd50 = getSellBreakdown(pack, pack.payToken, pv);
                      const bd75 = getSellBreakdown(pack, 4, pv);
                      const tierAccent = TIER_DATA[pack.tier].accent;
                      return (
                        <motion.div key={pi} className="CPP__cashout-pack-row"
                          initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:pi*0.05}}
                          style={{ borderColor: `${tierAccent}35` }}
                        >
                          <div className="CPP__cashout-pack-info">
                            <span className="tier" style={{color:tierAccent}}>{TIER_DATA[pack.tier].name}</span>
                            <span className="cards">{pack.cards.length} cards</span>
                            <span className="val green">${pv.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                            <span className="date">{new Date(pack.timestamp).toLocaleDateString()}</span>
                          </div>
                          <div className="CPP__cashout-pack-vals">
                            <div className="CPP__cashout-pack-val token">
                              <span className="lbl">Sell 50%</span>
                              <span className="amt">{bd50.tokenBack.toFixed(4)} {bd50.tokenSymbol}</span>
                            </div>
                            <div className="CPP__cashout-pack-val gem">
                              <span className="lbl">Sell 75%</span>
                              <span className="amt">{bd75.gemsBack.toFixed(0)} GEM</span>
                            </div>
                          </div>
                          <div className="CPP__cashout-pack-acts">
                            <button className="CPBtn sell sm" onClick={() => openCashout(pack, "sell")}>
                              💰 Sell Back
                            </button>
                            <button className="CPBtn ship sm" onClick={() => openCashout(pack, "ship")}>
                              📦 Ship
                            </button>
                          </div>
                        </motion.div>
                      );
                    })
                  )}

                  {/* Already redeemed */}
                  {history.filter(p => p.soldBack || p.shipped).length > 0 && (
                    <div className="CPP__cashout-redeemed-section">
                      <h4 className="CPP__cashout-redeemed-label">Redeemed</h4>
                      {history.filter(p => p.soldBack || p.shipped).map((pack, pi) => (
                        <div key={pi} className="CPP__cashout-pack-row redeemed">
                          <div className="CPP__cashout-pack-info">
                            <span className="tier">{TIER_DATA[pack.tier].name}</span>
                            <span className="date">{new Date(pack.timestamp).toLocaleDateString()}</span>
                          </div>
                          {pack.soldBack && <span className="CPBadge redeemed">Sold Back ✓</span>}
                          {pack.shipped && (
                            <div style={{display:"flex",flexDirection:"column",gap:"0.2rem"}}>
                              <span className="CPBadge shipped">
                                {pack.shipStatus === "delivered" ? "Delivered ✓" : "📦 In Transit"}
                              </span>
                              {pack.trackingRef && (
                                <span style={{fontSize:"0.72rem",color:"#6b7099"}}>Ref: {pack.trackingRef}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* ════ REFERRAL ════ */}
          {phase === "referral" && (
            <motion.div key="referral" className="CPP__referral" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>

              {/* ── Daily GEM Bonus ── */}
              {account && (
                <div className="CPP__daily-bonus-card">
                  <div className="CPP__daily-bonus-header">
                    <div className="CPP__daily-bonus-icon">💎</div>
                    <div>
                      <h3 className="CPP__daily-bonus-title">Daily GEM Bonus</h3>
                      <p className="CPP__daily-bonus-sub">
                        {canClaimBonus
                          ? "Your bonus is ready to claim!"
                          : nextClaimAt != null
                          ? `Next claim: ${new Date(nextClaimAt).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}`
                          : "Open a pack first to unlock daily bonuses"}
                      </p>
                    </div>
                  </div>
                  <motion.button
                    className={`CPP__daily-bonus-btn${canClaimBonus ? " ready" : ""}`}
                    disabled={!canClaimBonus || isClaimingBonus}
                    onClick={handleClaimDailyBonus}
                    whileHover={canClaimBonus ? {scale:1.03} : {}}
                    whileTap={canClaimBonus ? {scale:0.97} : {}}
                  >
                    {isClaimingBonus ? "Claiming…" : canClaimBonus ? "💎 Claim Daily GEMs" : "⏳ Come back tomorrow"}
                  </motion.button>
                  {nextClaimAt != null && !canClaimBonus && (
                    <div className="CPP__daily-countdown">
                      <div className="CPP__daily-countdown-bar">
                        <div className="CPP__daily-countdown-fill" style={{
                          width: `${Math.max(0, Math.min(100, ((Date.now() - (nextClaimAt - 86400000)) / 86400000) * 100))}%`
                        }} />
                      </div>
                      <span className="CPP__daily-countdown-label">24h cooldown</span>
                    </div>
                  )}
                </div>
              )}

              {/* Hero + Tier Badge */}
              <div className="CPP__ref-hero">
                <div className="CPP__ref-tier-badge" style={{color: refTier.color, background: refTier.bg, borderColor: refTier.color + "55"}}>
                  {refTier.label} Affiliate · {refTier.t1}% Tier 1{refTier.t2 > 0 ? ` · ${refTier.t2}% Tier 2` : ""}
                </div>
                <h2 className="CPP__ref-title">Refer Friends &amp; Earn</h2>
                <p className="CPP__ref-sub">
                  Earn <strong style={{color: refTier.color}}>{refTier.t1}% commission</strong> on every pack your Tier 1 referrals open.
                  {refTier.t2 > 0 && <> Plus <strong style={{color:"#a5b4fc"}}>{refTier.t2}%</strong> from their referrals (Tier 2).</>}
                  {" "}Paid instantly in your choice of token. No minimum.
                </p>
              </div>

              {/* Stats */}
              <div className="CPP__ref-stats">
                <div className="CPP__ref-stat">
                  <span className="n">{t1Count}</span>
                  <span className="l">Tier 1 Refs</span>
                </div>
                <div className="CPP__ref-stat">
                  <span className="n">{t2Count}</span>
                  <span className="l">Tier 2 Refs</span>
                </div>
                <div className="CPP__ref-stat">
                  <span className="n">${totalEarned.toFixed(2)}</span>
                  <span className="l">Total Earned</span>
                </div>
                <div className="CPP__ref-stat claimable" style={{borderColor: claimable > 0 ? "#4ade8040":""}}>
                  <span className="n" style={{color: claimable > 0 ? "#4ade80":""}}>${claimable.toFixed(2)}</span>
                  <span className="l">Claimable</span>
                </div>
              </div>

              {/* Claim: token selector + button */}
              {account && (
                <>
                  {claimable > 0 && (
                    <div className="CPP__ref-claim-token">
                      <span className="CPP__ref-claim-token-label">Receive earnings in</span>
                      <div className="CPP__ref-claim-token-opts">
                        {TOKENS.filter(t => t.id <= 3).map(t => (
                          <button key={t.id}
                            className={`CPP__ref-claim-token-opt${refClaimToken === t.id ? " active" : ""}`}
                            onClick={() => setRefClaimToken(t.id)}
                          >
                            <span>{t.icon}</span>
                            <span>{t.symbol}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <motion.button
                    className={`CPP__ref-claim-btn${claimable > 0 ? " has-earnings" : ""}`}
                    disabled={claimable <= 0 || isClaiming}
                    onClick={handleClaimRef}
                    whileHover={claimable > 0 ? {scale:1.03} : {}}
                    whileTap={claimable > 0 ? {scale:0.97} : {}}
                  >
                    {isClaiming ? "Claiming…" : claimable > 0
                      ? `💰 Claim $${claimable.toFixed(2)} → ${["BUSDC","WBNB","WETH","BESC"][refClaimToken]}`
                      : "No earnings to claim yet"}
                  </motion.button>
                </>
              )}

              {/* Tier progression */}
              {nextTier && (
                <div className="CPP__ref-tier-progress">
                  <div className="CPP__ref-tier-progress-labels">
                    <span style={{color: refTier.color}}>{refTier.label}</span>
                    <span style={{color:"#6b7099"}}>{t1Count} / {nextTier.min} referrals to {nextTier.label}</span>
                    <span style={{color: nextTier.color}}>{nextTier.label}</span>
                  </div>
                  <div className="CPP__ref-tier-bar">
                    <motion.div className="CPP__ref-tier-fill"
                      initial={{width:0}} animate={{width:`${tierProgress}%`}}
                      transition={{duration:0.8,ease:"easeOut"}}
                      style={{background:`linear-gradient(90deg, ${refTier.color}, ${nextTier.color})`}}
                    />
                  </div>
                  <p className="CPP__ref-tier-hint">
                    {nextTier.min - t1Count} more referrals to unlock {nextTier.label} · {nextTier.t1}% T1{nextTier.t2 > 0 ? ` + ${nextTier.t2}% T2` : ""}
                  </p>
                </div>
              )}

              {/* Tier ladder */}
              <div className="CPP__ref-tier-ladder">
                <div className="CPP__ref-ladder-title">Affiliate Tiers</div>
                {REF_TIERS.map((tier) => {
                  const isActive = tier.label === refTier.label;
                  return (
                    <div key={tier.label} className={`CPP__ref-ladder-row${isActive?" active":""}`}
                      style={isActive ? {borderColor: tier.color+"55", background: tier.bg} : {}}>
                      <div className="CPP__ref-ladder-badge" style={{color: tier.color}}>
                        {tier.label}
                      </div>
                      <div className="CPP__ref-ladder-reqs">
                        {tier.min === 0 ? "Everyone starts here" : `${tier.min}+ Tier 1 referrals`}
                      </div>
                      <div className="CPP__ref-ladder-comm">
                        <span style={{color: tier.color}}>{tier.t1}% T1</span>
                        {tier.t2 > 0 && <span style={{color:"#a5b4fc"}}> + {tier.t2}% T2</span>}
                      </div>
                      {isActive && <span className="CPP__ref-ladder-you">← You</span>}
                    </div>
                  );
                })}
              </div>

              {/* Referral link */}
              <div className="CPP__ref-box">
                <div className="CPP__ref-label">Your Referral Link</div>
                <div className="CPP__ref-link-row">
                  <span className="CPP__ref-link-text">{account ? refLink : "Connect wallet to get your link"}</span>
                  {account && (
                    <button className={`CPP__ref-copy${refCopied?" copied":""}`} onClick={copyRefLink}>
                      {refCopied ? "Copied ✓" : "Copy"}
                    </button>
                  )}
                </div>
                {account && (
                  <div className="CPP__ref-code-row">
                    <span>Code: </span>
                    <strong style={{color:"#4ade80"}}>{refCode}</strong>
                  </div>
                )}
              </div>

              {/* ── Vanity Referral Code ── */}
              <div className="CPP__ref-vanity">
                <div className="CPP__ref-vanity-title">Your Referral Code</div>

                {myVanityCode || refVanityCode ? (
                  <div className="CPP__ref-vanity-claimed">
                    <span className="CPP__ref-vanity-code" style={{color:"#4ade80"}}>
                      🏷️ {myVanityCode ?? refVanityCode}
                    </span>
                    <p className="CPP__ref-vanity-hint">
                      Share this code — friends enter it in the promo code field to register you as their referrer.
                    </p>
                  </div>
                ) : account ? (
                  <div className="CPP__ref-vanity-form">
                    <p className="CPP__ref-vanity-desc">
                      Claim a custom code so friends can register you as their referrer (instead of your wallet address). One-time, permanent.
                    </p>
                    <div className="CPP__ref-vanity-input-row">
                      <input
                        className="CPP__ref-vanity-input"
                        placeholder="e.g. POKEMON_KING"
                        value={refCodeInput}
                        onChange={(e) => setRefCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,""))}
                        maxLength={20}
                      />
                      <button
                        className="CPBtn ship sm"
                        disabled={!refCodeInput.trim() || isSettingCode}
                        onClick={handleSetReferralCode}
                      >
                        {isSettingCode ? "Claiming…" : "Claim →"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{color:"#6b7099",fontSize:"0.85rem"}}>Connect wallet to claim a vanity code.</p>
                )}
              </div>

              {/* ── Register a referrer by code ── */}
              {account && !myReferrer && (
                <div className="CPP__ref-register">
                  <div className="CPP__ref-register-title">Have a Referral Code?</div>
                  <p className="CPP__ref-register-desc">
                    Enter your referrer's code to register them. Must be done before your first pack open.
                  </p>
                  <div className="CPP__ref-vanity-input-row">
                    <input
                      className="CPP__ref-vanity-input"
                      placeholder="e.g. POKEMON_KING"
                      value={refCodeByInput}
                      onChange={(e) => setRefCodeByInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,""))}
                      maxLength={20}
                    />
                    <button
                      className="CPBtn ship sm"
                      disabled={!refCodeByInput.trim() || isRegByCode}
                      onClick={handleRegisterByCode}
                    >
                      {isRegByCode ? "Registering…" : "Register →"}
                    </button>
                  </div>
                </div>
              )}

              {/* Share buttons */}
              <div className="CPP__ref-share">
                <button className="CPP__ref-share-btn twitter"
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`🎴 I'm opening real Pokémon packs on BESC Draw and earning crypto! Join with my link: ${refLink} 🔥`)}`, "_blank")}
                >𝕏 Share on X</button>
                <button className="CPP__ref-share-btn tg"
                  onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent("Open real Pokémon TCG packs and earn crypto on BESC Draw 🎴")}`, "_blank")}
                >✈️ Share on Telegram</button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* Sidebar */}
      <aside className="CPP__sidebar">
        <LiveFeed />
      </aside>
      </div>

      {/* Info strip */}
      <div className="CPP__info-strip">
        {[
          ["🔒","Non-custodial · All on-chain"],
          ["♻️","7-day sell-back at card FMV"],
          ["⚔️","Live 1v1 Battles PvP"],
          ["📦","Physical worldwide shipping"],
          ["💹","Live Pokémon TCG prices"],
          ["💎","Earn GEM on every open"],
        ].map(([icon,label]) => (
          <div key={String(label)} className="CPP__info-item">
            <span>{icon}</span><span>{label}</span>
          </div>
        ))}
      </div>

      <Footer />

      {/* ── Battle Arena overlay ── */}
      <AnimatePresence>
        {activeBattle && (
          <motion.div
            key="battle-arena"
            style={{ position: "fixed", inset: 0, zIndex: 3000 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            <BattleArena
              battle={activeBattle}
              account={account}
              onClose={() => setActiveBattle(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
